import { useState, useEffect, useMemo } from 'react';

// TODO: support all V2 filter operators, validation, and filter persistence.
const ALL_API_FILTER_OPTIONS = [
  { value: 'search', label: 'Name', type: 'text', isClientSide: true },
  { value: 'contact_source', label: 'Advisor', type: 'select', isClientSide: true },
  { value: 'first_name', label: 'First Name', type: 'text' },
  { value: 'last_name', label: 'Last Name', type: 'text' },
  { value: 'company_name', label: 'Company Name', type: 'text' },
  { value: 'position_name', label: 'Position Name', type: 'text' },
  { value: 'current_company_name', label: 'Current Company Name', type: 'text' },
  { value: 'city_name', label: 'City Name', type: 'text' },
  { value: 'country_name', label: 'Country Name', type: 'text' },
];

export const useFilters = () => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterContact, setFilterContact] = useState('');
  const [activeApiFilters, setActiveApiFilters] = useState([]);
  const [sortBy, setSortBy] = useState('');

  const [showNewFilterDropdown, setShowNewFilterDropdown] = useState(false);
  const [newFilterType, setNewFilterType] = useState('');
  const [newFilterValue, setNewFilterValue] = useState('');

  useEffect(() => {
    const searchDebounceTimeout = setTimeout(() => {
      setSearch(searchInput);
    }, 300);

    return () => clearTimeout(searchDebounceTimeout);
  }, [searchInput]);

  const activeFiltersSummary = useMemo(() => {
    const filters = [];
    
    if (search && search.trim()) {
      filters.push({ type: 'Search', value: search.trim() });
    }
    
    if (filterContact) {
      filters.push({ type: 'Provider', value: filterContact });
    }
    
    activeApiFilters.forEach(filter => {
      if (filter.field && filter.value) {
        const fieldLabel = ALL_API_FILTER_OPTIONS.find(opt => opt.value === filter.field)?.label || filter.field;
        filters.push({ type: fieldLabel, value: filter.value });
      }
    });
    
    if (sortBy) {
      const sortLabel = {
        first_name: 'First Name',
        last_name: 'Last Name', 
        company_name: 'Company',
        position_name: 'Position',
        city_name: 'City',
        country_name: 'Country'
      }[sortBy] || sortBy;
      filters.push({ type: 'Sort by', value: sortLabel });
    }
    
    return filters;
  }, [search, filterContact, activeApiFilters, sortBy]);

  const usedFilterTypes = useMemo(() => {
    const used = [];
    if (search && search.trim()) used.push('search');
    if (filterContact) used.push('contact_source');
    activeApiFilters.forEach(filter => used.push(filter.field));
    if (sortBy) used.push('sort');
    return used;
  }, [search, filterContact, activeApiFilters, sortBy]);

  const addNewFilter = () => {
    if (!newFilterType || !newFilterValue.trim()) return;
    
    const filterOption = ALL_API_FILTER_OPTIONS.find(opt => opt.value === newFilterType);
    if (!filterOption) return;

    if (filterOption.isClientSide) {
      if (newFilterType === 'search') {
        setSearch(newFilterValue.trim());
        setSearchInput(newFilterValue.trim());
      } else if (newFilterType === 'contact_source') {
        setFilterContact(newFilterValue.trim());
      }
    } else {
      const existingFilterIndex = activeApiFilters.findIndex(f => f.field === newFilterType);
      if (existingFilterIndex >= 0) {
        setActiveApiFilters(prev => 
          prev.map((f, i) => i === existingFilterIndex 
            ? { ...f, value: newFilterValue.trim() }
            : f
          )
        );
      } else {
        setActiveApiFilters(prev => [...prev, { field: newFilterType, value: newFilterValue.trim() }]);
      }
    }
    
    setNewFilterType('');
    setNewFilterValue('');
    setShowNewFilterDropdown(false);
  };

  const removeFilter = (filterType, filterValue) => {
    if (filterType === 'Search') {
      setSearch('');
      setSearchInput('');
    } else if (filterType === 'Provider') {
      setFilterContact('');
    } else if (filterType === 'Sort by') {
      setSortBy('');
    } else {
      const filterField = ALL_API_FILTER_OPTIONS.find(opt => opt.label === filterType)?.value;
      if (filterField) {
        setActiveApiFilters(prev => prev.filter(f => f.field !== filterField));
      }
    }
  };

  const resetAllFilters = () => {
    setSearch('');
    setSearchInput('');
    setFilterContact('');
    setActiveApiFilters([]);
    setSortBy('');
    setNewFilterType('');
    setNewFilterValue('');
    setShowNewFilterDropdown(false);
  };

  return {
    search,
    setSearch,
    searchInput,
    setSearchInput,
    filterContact,
    setFilterContact,
    activeApiFilters,
    setActiveApiFilters,
    sortBy,
    setSortBy,
    
    showNewFilterDropdown,
    setShowNewFilterDropdown,
    newFilterType,
    setNewFilterType,
    newFilterValue,
    setNewFilterValue,
    
    activeFiltersSummary,
    usedFilterTypes,
    ALL_API_FILTER_OPTIONS,
    
    addNewFilter,
    removeFilter,
    resetAllFilters,
  };
};

export default useFilters;
